import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import Icon from '../../../../../components/Icon'
import BookOpenIcon from '../../../../../assets/icons/book.svg';
import whatsappIcon from '../../../../../assets/icons/whatsapp.svg';
import telegramIcon from '../../../../../assets/icons/telegram.svg';
import ShareIcon from '../../../../../assets/icons/share.svg';
import PlusIcon from '../../../../../assets/icons/add-bold.svg';
import ServerIcon from '../../../../../assets/icons/server.svg';
import ShowIcon from '../../../../../assets/icons/show.svg';
import DotsVertical from '../../../../../assets/icons/dots-vertical.svg';
import { connect, useDispatch } from "react-redux";
import { showModal } from "../../../../../store/actions/modal";
import { useWindowSize } from "../../../../../hooks/useWindowSize";
import constants from "../../../../../constants";

type MenuItem = {
  label: string;
  icon: React.ReactNode;
  action: any;
  iconBgColor: string;
  labelBgColor: string;
  type: 'button' | 'link'
  disabled?: boolean
};

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.2); // Podés ajustar la opacidad si querés que sea más sutil
  z-index: 100;
`;

const Container = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  z-index: 120;
`;

const FabButton = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: #2f2f2f;
  color: white;
  border: none;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MenuButton = styled(motion.button) <{ labelBgColor: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
  border-radius: 9999px;
  background: ${({ labelBgColor }) => labelBgColor ? labelBgColor : "white"};
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  font-weight: 600;
  font-size: 16px;
  color: #2f2f2f;
  border: none;
  cursor: pointer;
`;

const MenuAnchor = styled(motion.a) <{ labelBgColor: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
  border-radius: 9999px;
  background: ${({ labelBgColor }) => labelBgColor || "white"};
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  font-weight: 600;
  font-size: 16px;
  color: #2f2f2f;
  border: none;
  text-decoration: none;
  cursor: pointer;
`;

const IconWrapper = styled.div<{ iconBgColor: string }>`
  background-color: ${({ iconBgColor }) => iconBgColor};
  padding: 6px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FloatingMenu = ({
  setShowNewPlanSidebar,
  group,
  userInformation,
  // setCategoryManagerVisible,
  // setCreateNewCategorySidebarVisible
}) => {
  const { isMobile } = useWindowSize();
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch()

  const previewLink = useMemo(() => {
    return `/group/${group?.slug}/library`
  }, [group])

  const chatLink = useMemo(() => {
    return (!!group?.telegram_groups &&
      !!group?.telegram_groups[0]) &&
      group?.telegram_groups[0]?.main_invitation_link
  }, [group])

  const copyUrl = useCallback(() => {
    const link = `${process.env.REACT_APP_SITE_BASE_URL}/group/${group?.slug}`;
    navigator.clipboard.writeText(link);
    setTimeout(() => {
      dispatch(showModal("modal-share-group-link"));
    }, 500)
  }, [group])

  const newPostLink = useMemo(() => {
    return group?.group_type === "free_experience"
      ? `/dashboard/community/events/${group?.id}/posts/v2/new`
      : `/dashboard/${group?.group_type}/${group?.id}/posts/v2/new`
  }, [group])

  const supportUrl = useMemo(() => {
    const { whatsapp } = constants;
    if (!whatsapp.enabled) return null;

    const expertName = userInformation?.expert?.username;
    const message = expertName
      ? whatsapp.expert_message.replace("{name}", expertName)
      : whatsapp.message;

    return `${whatsapp.url}?phone=${whatsapp.number}&text=${encodeURIComponent(message)}`;
  }, [userInformation]);

  const menuItems: MenuItem[] = useMemo(() => [
    {
      label: `Agregar Precio`,
      icon: <Icon icon={PlusIcon} size="14px" color="#2D3436" />,
      action: () => setShowNewPlanSidebar(true),
      type: 'button',
      iconBgColor: "rgb(169, 246, 255)",
      labelBgColor: "white",
    },
    ...(!!group?.telegram_groups && !!group?.telegram_groups[0] ? [
      {
        label: `Ir a canal de chat`,
        icon: <Icon icon={group?.telegram_groups[0]?.platform === "whatsapp" ? whatsappIcon : telegramIcon} size="14px" color="#2D3436" />,
        action: chatLink,
        type: 'link' as const,
        iconBgColor: "transparent",
        labelBgColor: "white",
      },
    ] as MenuItem[] : []
    ),
    {
      label: "Vista previa",
      icon: <Icon icon={ShowIcon} size="16px" color="#2D3436" />,
      action: previewLink,
      type: 'link',
      iconBgColor: "transparent",
      labelBgColor: "white"
    },
    {
      label: `Compartir ${group?.group_type === "experience" ? "taller" : "membresía"}`,
      icon: <Icon icon={ShareIcon} size="16px" color="#2D3436" />,
      action: () => copyUrl(),
      type: 'button',
      iconBgColor: "transparent",
      labelBgColor: "white",
    },
    {
      label: "Ir a soporte",
      icon: <Icon icon={whatsappIcon} size="16px" color="#2D3436" />,
      action: supportUrl,
      type: 'link',
      iconBgColor: "transparent",
      labelBgColor: "#68E7B0",
      disabled: !supportUrl
    },
  ], [group, previewLink, copyUrl, newPostLink, supportUrl, chatLink])

  const handleItemClick = (item: MenuItem) => {
    if (item.type === 'button') {
      if (typeof item.action === 'function') {
        item.action();
      }
    } else if (item.type === 'link' && typeof item.action === 'string') {
      // Se deja vacío ya que el <a> se encarga de redireccionar
    }

    // Cerrar el menú en todos los casos
    setOpen(false);
  };


  if (!isMobile) {
    return <></>;
  }

  return (
    <>
      {open && (
        <Backdrop
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setOpen(false)}
        />
      )}

      <Container className="floating-menu">
        <AnimatePresence>
          {open && (
            <>
              {menuItems.map((item, i) => {
                const animationProps = {
                  key: item.label,
                  initial: { opacity: 0, y: 20 },
                  animate: { opacity: 1, y: 0 },
                  exit: { opacity: 0, y: 20 },
                  transition: { delay: i * 0.05 },
                };

                if (item.type === 'link' && typeof item.action === 'string') {
                  return (
                    <MenuAnchor
                      href={item.disabled ? undefined : item.action}
                      target="_blank"
                      rel="noopener noreferrer"
                      {...animationProps}
                      labelBgColor={item.labelBgColor}
                      onClick={(e) => {
                        if (item.disabled) {
                          e.preventDefault();
                          return;
                        }
                        handleItemClick(item);
                      }}
                      style={{
                        opacity: item.disabled ? 0.5 : 1,
                        cursor: item.disabled ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {item.label}
                      <IconWrapper iconBgColor={item.iconBgColor}>{item.icon}</IconWrapper>
                    </MenuAnchor>
                  );
                }

                return (
                  <MenuButton
                    {...animationProps}
                    labelBgColor={item.labelBgColor}
                    disabled={item.disabled}
                    onClick={() => {
                      item.action()
                      handleItemClick(item)
                    }}
                  >
                    {item.label}
                    <IconWrapper iconBgColor={item.iconBgColor}>{item.icon}</IconWrapper>
                  </MenuButton>
                );
              })}
            </>
          )}
        </AnimatePresence>

        <FabButton onClick={() => setOpen((prev) => !prev)}>
          <Icon icon={DotsVertical} size="24px" />
        </FabButton>
      </Container>
    </>
  );
}


const state = ({ groupStore, categoryStore, userStore }) => {
  const { data: allCategories, states: allCategoriesState } = categoryStore.allCategories;
  const { data: group, states: groupStates } = groupStore.group;
  const { data: userInformation } = userStore.information || {};

  return {
    group,
    groupStates,
    allCategories,
    allCategoriesState,
    userInformation,
  };
};

export default connect(state)(FloatingMenu);
